import type { BuildEvent } from '@/api/schemas';
import { formatDateTime, formatDuration } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { wordingFor } from '@/lib/audience';
import { useSeesProviders } from '@/lib/useAudience';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { StatusPill, Tag } from '@/components/badges';
import { Skeleton } from '@/components/ui/skeleton';
import { anyStatusLabel, phaseLabel } from '../buildLabels';
import { describe, eventDotTone, eventSourceTag, kindIcon, phaseIcon, phaseTone, phasesOf, sortedByTime, type Phase } from './timelineModel';

export { phasesOf } from './timelineModel';

interface TimelineProps {
  events: BuildEvent[] | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  now: number;
}

export function BuildTimeline({ events, isLoading, error, onRetry, now }: TimelineProps) {
  if (isLoading && !events) return <TimelineSkeleton />;
  if (error && !events) return <ErrorState compact error={error} onRetry={onRetry} />;
  if (!events || events.length === 0) return <EmptyState compact icon="history" title="No events yet" description="Status changes, phases, notes and checks appear here as the build moves." />;
  const phases = phasesOf(events, now);
  return (
    <div className="flex flex-col gap-4 p-4 sm:p-5" data-testid="build-timeline">
      {phases.length > 0 ? <PhasesList phases={phases} /> : null}
      <ol className="relative flex flex-col gap-0.5 border-l pl-5">
        {sortedByTime(events).map((e, i) => (
          <EventRow key={e.id} event={e} index={i} />
        ))}
      </ol>
    </div>
  );
}

function TimelineSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-4" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-7 rounded-full" />
          <Skeleton className="h-3.5 w-56" />
          <Skeleton className="ml-auto h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

function PhasesList({ phases }: { phases: Phase[] }) {
  return (
    <div className="flex flex-col gap-2">
      <Kicker className="text-subtle">Phases</Kicker>
      <ol className="flex flex-wrap gap-2">
        {phases.map((p, i) => (
          <PhaseChip key={`${p.name}-${i}`} phase={p} />
        ))}
      </ol>
    </div>
  );
}

function PhaseChip({ phase: p }: { phase: Phase }) {
  return (
    <li data-testid={`build-phase-${p.name}`} className={cn('flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs', phaseTone(p))}>
      <Icon name={phaseIcon(p)} size={14} className={p.finishedAt ? undefined : 'pulse rounded-full'} />
      {phaseLabel(p.name)}
      <span className="tabular font-mono opacity-80">{formatDuration(p.durationSeconds)}</span>
    </li>
  );
}

function EventRow({ event: e, index }: { event: BuildEvent; index: number }) {
  const status = e.kind === 'status_changed' ? e.toStatus : undefined;
  return (
    <li data-testid="build-event" data-kind={e.kind} style={stagger(Math.min(index, 12))} className="enter relative flex items-start gap-3 py-2">
      <span className={cn('absolute -left-[29px] top-2.5 flex size-4 items-center justify-center rounded-full border bg-card', eventDotTone(status))}>
        <Icon name={kindIcon(e.kind)} size={12} />
      </span>
      <EventBody event={e} status={status} />
      <time dateTime={e.createdAt} className="tabular shrink-0 font-mono text-[11px] text-subtle" title={e.providerEventAt ? `provider: ${formatDateTime(e.providerEventAt)}` : undefined}>
        {formatDateTime(e.createdAt)}
      </time>
    </li>
  );
}

/** The event in plain words; API text that names a provider is reworded for clients (SPEC decision 23). */
function EventBody({ event: e, status }: { event: BuildEvent; status: string | null | undefined }) {
  const sees = useSeesProviders();
  const say = wordingFor(sees);
  const d = describe(e);
  const source = eventSourceTag(e.source, sees);
  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-foreground">
        <span>{say(d.title)}</span>
        {status ? <StatusPill status={status} label={anyStatusLabel(status)} still /> : null}
        {source ? <Tag>{source}</Tag> : null}
      </div>
      {d.detail ? <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{say(d.detail)}</p> : null}
    </div>
  );
}
